import {useEffect} from 'react'
import PropTypes from 'prop-types'
import compose from 'recompose/compose'
import withUserProfile from '@/lib/hoc/withUserProfile'
import Button from '@/components/shared/Button'
import LoadBar from '@/components/shared/LoadBar'
import Send from './steps/Send'
import Result from './steps/Result'
import classNames from 'classnames'
import modalStyles from '@/styles/organisms/Modal/styles.module.css'
import styles from '@/styles/templates/StepByStep/styles.module.css'

const StepByStep = ({
  userProfile,
  steps,
  step,
  result,
  stepsWithNoUI,
  navigateToStep,
  backButtonLabel,
  fwdButtonLabel,
  isBackButtonDisabled,
  isFwdButtonDisabled,
  renderCompleted,
  isModal,
  onBackButtonClick,
  onFwdButtonClick,
  onHandleLastFwdClick
}) => {
  function navigateTo(toStep) {
    if (steps[toStep] || stepsWithNoUI.includes(toStep)) {
      navigateToStep(toStep)
    }
  }

  const getStepsKeys = () => Object.keys(steps)
  const getStepIndex = () => getStepsKeys().findIndex((item) => item === step)
  const getLabel = () => (steps[step] ? steps[step].label : ' ')
  const getPercentage = () => {
    const k = getStepsKeys()
    const i = getStepIndex()
    return i < 1 ? 100 / k.length : (i / (k.length - 1)) * 100
  }
  const getStepComponent = () => {
    if (step === 'completed' || step === 'success') {
      return renderCompleted ? renderCompleted : Result
    } else if (step === 'error') {
      return Result
    } else if (step === 'send') {
      return Send
    } else {
      return steps[step].component
    }
  }
  const getStepProps = () => {
    const stepProps = (steps[step] && steps[step].stepProps) || {
      navigateTo,
      steps: getStepsKeys(),
      resultSetup: result
    }
    return {
      ...stepProps,
      step
    }
  }

  const isHeaderVisible = () => !stepsWithNoUI.includes(step)
  const isFooterVisible = () => !stepsWithNoUI.includes(step)
  const isOnLastStep = () => getStepsKeys().length === getStepIndex() + 1

  function handleBackButtonClick() {
    const {stepBack} = steps[step]
    if (stepBack) {
      navigateTo(stepBack)
    }
    if (onBackButtonClick) {
      onBackButtonClick()
    }
  }

  function handleFwdButtonClick() {
    if (isOnLastStep() && onHandleLastFwdClick) {
      onHandleLastFwdClick()
    } else {
      const {stepFwd} = steps[step]
      if (stepFwd) {
        navigateTo(stepFwd)
      }
    }
    if (onFwdButtonClick) {
      onFwdButtonClick()
    }
  }

  useEffect(() => {
    if (
      userProfile &&
      userProfile.id &&
      isOnLastStep() &&
      steps &&
      Object.keys(steps) &&
      Object.keys(steps).length > 1
    ) {
      navigateTo('send')
    }
  }, [userProfile])

  const StepComponent = getStepComponent()

  return (
    <div className={styles.ecStepByStep}>
      <div
        className={classNames({
          [modalStyles.ecModal__header]: true,
          [modalStyles.ecModal__header_hide]: !isHeaderVisible(),
          [modalStyles.ecModal__header_modal]: isModal
        })}
      >
        <p>{getLabel()}</p>
        <LoadBar percentage={getPercentage()} />
      </div>
      <div
        className={classNames({
          [styles.ecStepByStep__content]: true,
          [styles.ecStepByStep__content_modal]: isModal
        })}
      >
        {StepComponent && <StepComponent {...getStepProps()} />}
      </div>
      <div
        className={classNames({
          [modalStyles.ecModal__footer]: true,
          [modalStyles.ecModal__footer_hide]: !isFooterVisible(),
          [modalStyles.ecModal__footer_fixed]: !isModal
        })}
      >
        <div
          className={classNames({
            [modalStyles.ecModal__footer__buttons]: true,
            [styles.ecStepByStep__footer__buttons]: true
          })}
        >
          <Button
            disabled={isBackButtonDisabled}
            onClick={handleBackButtonClick}
          >
            {backButtonLabel}
          </Button>
          <Button
            active
            disabled={isFwdButtonDisabled}
            onClick={handleFwdButtonClick}
          >
            {fwdButtonLabel}
          </Button>
        </div>
      </div>
    </div>
  )
}

StepByStep.defaultProps = {
  stepsWithNoUI: ['intro', 'send', 'success', 'error', 'completed']
}

StepByStep.propTypes = {
  step: PropTypes.string.isRequired,
  steps: PropTypes.objectOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      component: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
      stepBack: PropTypes.string || null,
      stepFwd: PropTypes.string.isRequired
    })
  ).isRequired,
  result: PropTypes.object.isRequired,
  stepsWithNoUI: PropTypes.arrayOf(PropTypes.string.isRequired),
  backButtonLabel: PropTypes.string.isRequired,
  fwdButtonLabel: PropTypes.string.isRequired,
  isBackButtonDisabled: PropTypes.bool.isRequired,
  isFwdButtonDisabled: PropTypes.bool.isRequired,
  isModal: PropTypes.bool,
  onBackButtonClick: PropTypes.func,
  onFwdButtonClick: PropTypes.func,
  onHandleLastFwdClick: PropTypes.func
}

export default compose(withUserProfile)(StepByStep)
